import React, { useMemo } from 'react';
import { X as XIcon } from 'lucide-react';
import { useI18n } from '../../../i18n/I18nProvider';
import { getNotifConfig } from '../notificationIcons';
import type { NotificationAction, NotificationItem } from '../api';
import './NotificationList.css';
import { formatAppDate } from '../../../shared/utils/dates';

/** Types whose repeats about the same thing fold into one row: "Ada clapped (+4 more)". */
const COLLAPSIBLE = new Set(['kudos_reaction', 'kudos_comment', 'event_rsvp']);

interface Entry {
  item: NotificationItem;
  /** Folded repeats, newest first after `item`. */
  more: NotificationItem[];
}

export function relativeTime(iso: string | null, t: (k: string, v?: any) => string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('notifications.justNow');
  if (mins < 60) return t('notifications.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('notifications.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('notifications.daysAgo', { count: days });
  return formatAppDate(iso, { month: 'short', day: 'numeric' });
}

function isToday(iso: string | null) {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

/** In the last seven days, but not today. */
function isThisWeek(iso: string | null) {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 7 * 86400000;
}

type SectionKey = 'today' | 'thisWeek' | 'earlier';

/**
 * Today / Earlier sections (the bell's small panel), or Today / This week / Earlier
 * with `week` (the full page, spec §5 Notifications), with repeats folded inside each.
 */
export function groupEntries(items: NotificationItem[], { week = false }: { week?: boolean } = {}): { key: SectionKey; entries: Entry[] }[] {
  const sections: { key: SectionKey; entries: Entry[] }[] = [];
  items.forEach((item) => {
    const key: SectionKey = isToday(item.created_at) ? 'today'
      : week && isThisWeek(item.created_at) ? 'thisWeek' : 'earlier';
    let section = sections[sections.length - 1];
    if (!section || section.key !== key) {
      section = { key, entries: [] };
      sections.push(section);
    }
    const last = section.entries[section.entries.length - 1];
    if (last && COLLAPSIBLE.has(item.type) && last.item.type === item.type
        && item.related_id && last.item.related_id === item.related_id) {
      last.more.push(item);
    } else {
      section.entries.push({ item, more: [] });
    }
  });
  return sections;
}

interface Props {
  items: NotificationItem[];
  emptyText: string;
  onOpen: (item: NotificationItem, more: NotificationItem[]) => void;
  onAction: (item: NotificationItem, action: NotificationAction) => void;
  onDismiss: (item: NotificationItem) => void;
  /** Group by Today / This week / Earlier (the full page) instead of Today / Earlier. */
  groupByWeek?: boolean;
}

/** "Mark all as read" turns the dots off top to bottom, 30ms apart, for the first eight. */
const STAGGER_ROWS = 8;

export default function NotificationList({ items, emptyText, onOpen, onAction, onDismiss, groupByWeek = false }: Props) {
  const { t } = useI18n();
  const sections = useMemo(() => groupEntries(items, { week: groupByWeek }), [items, groupByWeek]);
  let row = -1;

  if (items.length === 0) {
    return <p className="notif-list__empty">{emptyText}</p>;
  }

  return (
    <div className="notif-list">
      {sections.map((section) => (
        <section key={section.key} aria-label={t(`notifications.${section.key}`)}>
          <h3 className="notif-list__section">{t(`notifications.${section.key}`)}</h3>
          <ul className="notif-list__items">
            {section.entries.map(({ item, more }) => {
              row += 1;
              const { Icon } = getNotifConfig(item.type);
              const unread = !item.is_read || more.some((m) => !m.is_read);
              const title = more.length > 0
                ? t('notifications.andMore', { title: item.title, count: more.length })
                : item.title;
              return (
                <li
                  key={item.id}
                  className={`notif-row${unread ? ' notif-row--unread' : ''}`}
                  style={{ '--notif-row-i': Math.min(row, STAGGER_ROWS) } as React.CSSProperties}
                >
                  {/* The unread mark: a 6px purple dot on the left. Always drawn, so going
                      read fades it out instead of making it vanish. */}
                  <span className={`notif-row__dot${unread ? '' : ' notif-row__dot--off'}`} aria-hidden="true" />
                  <span className="notif-row__icon" aria-hidden="true"><Icon size={16} /></span>
                  <div className="notif-row__body">
                    <button
                      type="button"
                      className="notif-row__main"
                      onClick={() => onOpen(item, more)}
                      aria-label={`${unread ? `${t('notifications.unreadLabel')}: ` : ''}${title}`}
                    >
                      <span className="notif-row__title">{title}</span>
                      {item.message && <span className="notif-row__message">{item.message}</span>}
                      <span className="notif-row__meta">
                        {relativeTime(item.created_at, t)}
                        {!item.available && <> · {t('notifications.unavailable')}</>}
                        {item.action_state && <> · {t(`notifications.outcome.${item.action_state}`)}</>}
                      </span>
                    </button>
                    {item.actions?.length > 0 && (
                      <div className="notif-row__actions">
                        {item.actions.map((action) => (
                          <button
                            key={action.key}
                            type="button"
                            className={`notif-row__action${action.primary ? ' notif-row__action--primary' : ''}`}
                            onClick={() => onAction(item, action)}
                          >
                            {t(`notifications.actions.${action.key}`)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="notif-row__dismiss"
                    onClick={() => onDismiss(item)}
                    aria-label={t('notifications.dismiss')}
                  >
                    <XIcon size={12} />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
